import {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {ListChecks, ShieldCheck} from 'lucide-react';
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {
  useQcPolicyViolations,
  useQcReviewItems,
  useBulkApproveQcReview,
  useClassificationConfig
} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {
  type QcPolicyViolationQueueSummary,
  type QcReviewItem,
  type QcSubmissionType
} from '@/api/qcReview';
import {SensitiveMediaToggle} from '@/components/shared/SensitiveMedia';
import {QcBulkBar} from '@/components/qcReview/QcBulkBar';
import {QcQueueTable} from '@/components/qcReview/QcQueueTable';
import {QcReviewModal} from '@/components/qcReview/QcReviewModal';
import {QcAccuracySummary} from '@/components/qcReview/QcAccuracySummary';
import {QcHistory} from '@/components/qcReview/QcHistory';
import {FilterBarSlotProvider} from '@/components/layout/FilterBarSlot';
import {
  QcAnalystFilters,
  QcAnalystTable,
  type QcAnalystStatusFilter
} from '@/components/qcReview/QcAnalystTable';
import {isPolicyViolationType} from '@/lib/classificationType';
import {TablePanel} from '@/components/ui/table-panel';
import {showToast} from '@/lib/toast';

const SUBMISSION_TYPES: QcSubmissionType[] = ['Content', 'Actor'];

// Weekly QA's three tabs plus History, every decision already made. Configuration is who QC holds
// and in which verticals.
type Section = 'queue' | 'history' | 'accuracy' | 'configuration';

const SECTIONS: Array<{value: Section; label: string}> = [
  {value: 'queue', label: 'Queue'},
  {value: 'history', label: 'History'},
  {value: 'accuracy', label: 'Accuracy'},
  {value: 'configuration', label: 'Configuration'}
];

function sectionFrom(tab: string | null): Section {
  // `analysts` is what Configuration was called before, kept so existing links still land on it.
  return tab === 'history' || tab === 'accuracy'
    ? tab
    : tab === 'configuration' || tab === 'analysts'
      ? 'configuration'
      : 'queue';
}

export function QcReviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const analystFilter = searchParams.get('analyst');
  // The tab and vertical live in the URL, so a reload or a shared link lands on the same list. A
  // pending-count link from Configuration is `?analyst=` alone, so it always opens on Queue.
  const section = sectionFrom(searchParams.get('tab'));
  // The filter bar's slot, where the Accuracy tab portals its own controls.
  const [filterSlot, setFilterSlot] = useState<HTMLDivElement | null>(null);
  const selectedVertical = searchParams.get('vertical');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reviewItem, setReviewItem] = useState<QcReviewItem | null>(null);
  const [editOnOpen, setEditOnOpen] = useState(false);
  // Snapshot of the pending list for a "Review all" run and the position in it, as on Weekly QA: kept
  // apart from the live query so items don't reorder or vanish as each decision invalidates it.
  const [reviewQueue, setReviewQueue] = useState<QcReviewItem[] | null>(null);
  const [reviewIndex, setReviewIndex] = useState(0);
  // Held here rather than in QcAnalystTable because the controls render in the page's filter bar.
  const [analystSearch, setAnalystSearch] = useState('');
  const [analystStatus, setAnalystStatus] = useState<QcAnalystStatusFilter>('all');
  const bulkApprove = useBulkApproveQcReview();

  const {data: allVerticalSummaries = [], isLoading: loadingVerticals} = useQcPolicyViolations();
  const pendingFor = (v: QcPolicyViolationQueueSummary, type: QcSubmissionType) =>
    type === 'Actor' ? v.pending_actor : v.pending_content;
  const pendingByType = (type: QcSubmissionType) =>
    allVerticalSummaries.reduce((sum, v) => sum + pendingFor(v, type), 0);
  // Content/Actor is picked first and narrows the verticals. With no type in the URL it opens on
  // Content, unless only Actor items are waiting, so the queue never looks empty when it isn't.
  const submissionType: QcSubmissionType =
    searchParams.get('type') === 'Actor' ||
    (!searchParams.get('type') && pendingByType('Content') === 0 && pendingByType('Actor') > 0)
      ? 'Actor'
      : 'Content';
  const hasPending = allVerticalSummaries.some((v) => v.pending > 0);
  // The summary also lists verticals whose items are all reviewed; only ones with pending work of
  // the selected type get a tab.
  const verticalSummaries = allVerticalSummaries.filter((v) => pendingFor(v, submissionType) > 0);
  const classifications = useClassificationConfig();
  const configVerticals = (classifications.data ?? [])
    .filter((c) => isPolicyViolationType(c.type))
    .map((c) => c.name);
  // Opens on the first vertical with pending work, as Weekly QA does, unless the URL names one.
  const activeVertical = analystFilter
    ? null
    : (selectedVertical ?? verticalSummaries[0]?.policy_violation ?? null);

  const {data: queueItems, isLoading: loadingQueue} = useQcReviewItems(
    analystFilter
      ? {analyst: analystFilter, status: 'pending', page_size: 100}
      : activeVertical
        ? {
            policy_violation: activeVertical,
            submission_type: submissionType,
            status: 'pending',
            page_size: 100
          }
        : undefined
  );

  // A selection is only meaningful for the list it was made on.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [analystFilter, activeVertical, submissionType]);

  // Writes a change into the URL without adding a history entry, so Back leaves the page rather
  // than stepping through filter states.
  function update(change: (next: URLSearchParams) => void) {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        change(next);
        return next;
      },
      {replace: true}
    );
  }

  function selectSection(value: Section) {
    update((next) => {
      if (value === 'queue') {
        next.delete('tab');
      } else {
        next.set('tab', value);
      }
    });
  }

  function selectVertical(vertical: string | null) {
    update((next) => {
      if (vertical) {
        next.set('vertical', vertical);
      } else {
        next.delete('vertical');
      }
    });
  }

  function selectSubmissionType(type: QcSubmissionType) {
    update((next) => {
      next.set('type', type);
    });
  }

  function toggleRow(id: string) {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(id)) {
        next.add(id);
      }

      return next;
    });
  }

  function toggleAll() {
    const items = queueItems ?? [];
    setSelectedIds(
      items.length > 0 && items.every((item) => selectedIds.has(item.id))
        ? new Set()
        : new Set(items.map((item) => item.id))
    );
  }

  function startReviewAll() {
    const pending = queueItems ?? [];
    if (pending.length === 0) {
      return;
    }

    setEditOnOpen(false);
    setReviewQueue(pending);
    setReviewIndex(0);
    setReviewItem(pending[0] ?? null);
  }

  function handleReviewSubmitted() {
    if (reviewQueue) {
      const nextIndex = reviewIndex + 1;
      if (nextIndex < reviewQueue.length) {
        setEditOnOpen(false);
        setReviewIndex(nextIndex);
        setReviewItem(reviewQueue[nextIndex] ?? null);
        return;
      }

      showToast(`Reviewed all ${reviewQueue.length} items.`);
      setReviewQueue(null);
    }

    setReviewItem(null);
  }

  function clearAnalystFilter() {
    update((next) => {
      next.delete('analyst');
    });
  }

  async function handleApproveAllForAnalyst() {
    if (!analystFilter) {
      return;
    }

    try {
      const result = await bulkApprove.mutateAsync({analyst_email: analystFilter});
      showToast(`Approved ${result.approved_count} pending item(s) for ${analystFilter}.`);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to approve pending items.'), 'error');
    }
  }

  // A vertical filtered out from underneath the current selection (e.g. its last item was just
  // reviewed) shouldn't leave the page stuck showing an empty, unselectable tab. Not while the
  // summaries load, or a linked vertical would be dropped before its tab could appear.
  useEffect(() => {
    if (
      !analystFilter &&
      !loadingVerticals &&
      selectedVertical &&
      !verticalSummaries.some((v) => v.policy_violation === selectedVertical)
    ) {
      selectVertical(null);
    }
    // The selectVertical call reads the current params; only the check's inputs are tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analystFilter, loadingVerticals, selectedVertical, verticalSummaries]);

  return (
    <div className="text-foreground flex h-full w-full flex-col">
      {/* The page blurb, tabs and every filter for the active tab sit in one white bar over the
          page's grey, the same strip the Submissions page uses, so the controls read apart from
          the tables. */}
      <div className="bg-card border-border shrink-0 space-y-3 border-b px-5 py-3">
        <p className="text-muted-foreground text-xs">
          Risky submissions from analysts still in training are held here until a lead approves or
          rejects them. Nothing in this queue reaches the customer until it is approved.
        </p>

        <Tabs
          value={section}
          onValueChange={(value) => {
            selectSection(value as Section);
          }}>
          <TabsList>
            {SECTIONS.map((s) => (
              <TabsTrigger key={s.value} value={s.value}>
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* One filter row: the active tab's filters on the left, the blur toggle on the right -
            it is how the tables' media is shown, so it sits with the view controls as it does on
            Investigations and Content rather than in the page header. */}
        <div className="flex flex-wrap items-center gap-2">
          {section === 'queue' ? (
            analystFilter ? (
              <>
                <p className="text-xs">
                  Showing pending items for <span className="font-medium">{analystFilter}</span>
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={(queueItems?.length ?? 0) === 0 || bulkApprove.isPending}
                    onClick={() => void handleApproveAllForAnalyst()}>
                    {bulkApprove.isPending ? <Spinner /> : null}
                    Approve all pending{queueItems?.length ? ` (${queueItems.length})` : ''}
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={clearAnalystFilter}>
                    Clear filter
                  </Button>
                </div>
              </>
            ) : hasPending ? (
              <div className="flex min-w-0 flex-col gap-2">
                <ToggleGroup
                  variant="outline"
                  spacing={0}
                  size="sm"
                  aria-label="Submission type"
                  className="self-start"
                  value={[submissionType]}
                  onValueChange={(next) => {
                    const type = SUBMISSION_TYPES.find((t) => t === next[0]);
                    if (type) {
                      selectSubmissionType(type);
                    }
                  }}>
                  {SUBMISSION_TYPES.map((t) => (
                    <ToggleGroupItem key={t} value={t}>
                      {t}
                      <Badge variant={t === submissionType ? 'default' : 'secondary'}>
                        {pendingByType(t)}
                      </Badge>
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>

                {verticalSummaries.length > 0 ? (
                  <ToggleGroup
                    variant="outline"
                    spacing={0}
                    size="sm"
                    aria-label="Vertical"
                    className="flex-wrap self-start"
                    value={activeVertical ? [activeVertical] : []}
                    onValueChange={(next) => {
                      const vertical = next[0];
                      if (vertical) {
                        selectVertical(vertical);
                      }
                    }}>
                    {verticalSummaries.map((v) => (
                      <ToggleGroupItem key={v.policy_violation} value={v.policy_violation}>
                        {v.policy_violation}
                        <Badge
                          variant={v.policy_violation === activeVertical ? 'default' : 'secondary'}>
                          {pendingFor(v, submissionType)}
                        </Badge>
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                ) : null}
              </div>
            ) : null
          ) : section === 'history' || section === 'accuracy' ? (
            <div ref={setFilterSlot} className="flex min-w-0 flex-1 flex-wrap items-center gap-2" />
          ) : (
            <QcAnalystFilters
              search={analystSearch}
              statusFilter={analystStatus}
              onSearchChange={setAnalystSearch}
              onStatusFilterChange={setAnalystStatus}
            />
          )}
          <div className="ml-auto flex items-center gap-2">
            {section === 'queue' && (analystFilter || activeVertical) ? (
              <Button
                type="button"
                size="sm"
                disabled={(queueItems?.length ?? 0) === 0}
                onClick={startReviewAll}>
                <ListChecks />
                Review all{queueItems?.length ? ` (${queueItems.length})` : ''}
              </Button>
            ) : null}
            <SensitiveMediaToggle />
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {section === 'queue' ? (
          <div className="flex flex-col gap-4">
            {analystFilter ? null : loadingVerticals ? (
              <div className="text-muted-foreground flex h-32 items-center justify-center gap-2 text-xs">
                <Spinner />
                Loading verticals…
              </div>
            ) : hasPending ? null : (
              <Empty variant="outline" className="py-10">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ShieldCheck />
                  </EmptyMedia>
                  <EmptyTitle>No QC review items</EmptyTitle>
                  <EmptyDescription>
                    Once a trainee submits a risky classification, it will show up here for review.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}

            {analystFilter || activeVertical ? (
              <TablePanel>
                <QcBulkBar
                  selectedIds={selectedIds}
                  onClear={() => {
                    setSelectedIds(new Set());
                  }}
                />
                <QcQueueTable
                  items={queueItems ?? []}
                  isLoading={loadingQueue}
                  selectedIds={selectedIds}
                  onToggleRow={toggleRow}
                  onToggleAll={toggleAll}
                  onOpenReview={(item) => {
                    setEditOnOpen(false);
                    setReviewItem(item);
                  }}
                  onEditAndApprove={(item) => {
                    setEditOnOpen(true);
                    setReviewItem(item);
                  }}
                />
              </TablePanel>
            ) : null}
          </div>
        ) : section === 'history' ? (
          <FilterBarSlotProvider value={filterSlot}>
            <QcHistory verticals={configVerticals} />
          </FilterBarSlotProvider>
        ) : section === 'accuracy' ? (
          <FilterBarSlotProvider value={filterSlot}>
            <QcAccuracySummary />
          </FilterBarSlotProvider>
        ) : (
          <QcAnalystTable
            verticals={configVerticals}
            search={analystSearch}
            statusFilter={analystStatus}
          />
        )}
      </div>

      <QcReviewModal
        item={reviewItem}
        startInEditMode={editOnOpen}
        queuePosition={
          reviewQueue ? {current: reviewIndex + 1, total: reviewQueue.length} : undefined
        }
        onOpenChange={(open) => {
          if (!open) {
            setReviewQueue(null);
            setReviewItem(null);
          }
        }}
        onSubmitted={handleReviewSubmitted}
      />
    </div>
  );
}
